import { isFunc, isPromise } from "@/utils";
import { useSyncExternalStore } from "react";
class Store<T> {
  #state: T;
  readonly #watch?: (newState: T, oldState: T) => void;
  protected onStoreChanges: Array<() => void>;
  constructor(data: T, watch?: (newState: T, oldState: T) => void) {
    this.#state = data;
    this.onStoreChanges = [];
    this.#watch = watch;
    this.setState = this.setState.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.getState = this.getState.bind(this);
    this.initStore = this.initStore.bind(this);
    return this;
  }
  public getState() {
    return this.#state;
  }
  public subscribe(onStoreChange: () => void) {
    const index = this.onStoreChanges.push(onStoreChange) - 1;
    return () => {
      this.onStoreChanges.splice(index, 1);
    };
  }
  public setState(state: T): void;
  public setState(func: (state: T) => T): void;
  public setState(state: T | ((state: T) => T)) {
    const _state = isFunc(state) ? state(this.#state) : state;
    if (Object.is(state, this.#state)) {
      return;
    }
    if (this.#watch) {
      this.#watch(_state, this.#state);
    }
    this.#state = _state;
    this.onStoreChanges.forEach((res) => res());
  }

  /**
   * 初始化Store的函数 函数本身不控制是否调用 在createStore 中控制
   * @param initialState
   * @param updateRefresh
   */
  public initStore(initialState: T | Promise<T>, updateRefresh?: (isRefresh: boolean) => void) {
    updateRefresh && updateRefresh(false);
    if (isPromise(initialState)) {
      initialState.then(
        (res) => {
          this.setState(res);
        },
        () => {
          updateRefresh && updateRefresh(true);
        },
      );
    } else {
      this.setState(initialState);
    }
  }
}

type StoreExample<T> = [() => Store<T>, (immediate?: boolean) => void];
interface Options<T> {
  defaultState?: T;
  watch?: (newState: T, oldState: T) => void;
}
export function createStore<T>(initialState: T, options?: Options<T>): StoreExample<T>;
/**
 *
 * @param initialState  初始化函数  返回一个Promise
 * @param options
 */
export function createStore<T>(
  initialState: () => Promise<T>,
  options?: Options<T>,
): StoreExample<T>;
export function createStore<T>(initialState: () => T, options?: Options<T>): StoreExample<T>;
export function createStore<T>(
  initialState: T | (() => Promise<T>) | (() => T),
  options?: Options<T>,
) {
  let store: Store<T>;
  let isRefresh = true; // 判断是否 需要初始化缓存
  const updateRefresh = (is: boolean) => {
    isRefresh = is;
  };
  const getStore = () => {
    if (store) {
      if (isRefresh && isFunc(initialState)) {
        store.initStore(initialState(), updateRefresh);
      }
      return store;
    }
    // 创建流程
    if (isFunc(initialState)) {
      const state = initialState();
      if (isPromise(state)) {
        // 非 Promise 可以减少第一次 setState  与 effect 相关
        store = new Store(options?.defaultState as T, options?.watch);
        store.initStore(state, updateRefresh);
      } else {
        isRefresh = false;
        store = new Store(state, options?.watch);
      }
    } else {
      store = new Store(initialState, options?.watch);
    }
    return store;
  };
  /**
   *
   * @param immediate true 立即更新 默认false
   */
  const refresh = (immediate = false) => {
    isRefresh = true;
    if (immediate && store && isFunc(initialState)) {
      store.initStore(initialState(), updateRefresh);
    }
  };
  return [getStore, refresh];
}
export function useStore<T>([getStore]: StoreExample<T>) {
  const store = getStore();
  const state = useSyncExternalStore(store.subscribe, store.getState);
  return [state, store.setState] as const;
}
/**
 *  只取值 不set 订阅更新
 * @param param0  StoreExample 用createStore的 Store
 * @returns
 */
export function useStoreValue<T>([getStore]: StoreExample<T>) {
  const store = getStore();
  return useSyncExternalStore(store.subscribe, store.getState);
}
/**
 *  只set 不取值 不订阅更新
 * @param param0  StoreExample 用createStore的 Store
 * @returns  setState<T>(state:T)
 */
export function useSetStore<T>([getStore]: StoreExample<T>) {
  return getStore().setState;
}

/**
 * 刷新函数  (immediate = false) =>voide   immediate true 立即更新 默认false
 * @param param0
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useStoreRefresh<T>([_, refresh]: StoreExample<T>) {
  return refresh;
}
/**
 * 用于非React 环境 获取 Store 的值
 * @param param0  StoreExample 用createStore的 Store
 * @returns
 */
export const getStore = <T>([getStore]: StoreExample<T>) => {
  const store = getStore();
  return [store.getState(), store.setState] as const;
};
export const getSetStore = <T>([getStore]: StoreExample<T>) => {
  return getStore().setState;
};
export const getStoreValue = <T>([getStore]: StoreExample<T>) => {
  return getStore().getState();
};
